import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { facturasService } from '../services/facturasService';

const fmt = (v) =>
  Number(v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function ModalNuevaFactura({ onConfirmar, onCancelar, creando, errorCreacion }) {
  const hoy = new Date().toISOString().split('T')[0];
  const [fecha, setFecha]         = useState(hoy);
  const [numFactura, setNumFactura] = useState('');
  const [errorLocal, setErrorLocal] = useState(null);

  const handleConfirmar = () => {
    if (numFactura !== '') {
      const n = parseInt(numFactura, 10);
      if (isNaN(n) || n <= 0) {
        setErrorLocal('El número de factura debe ser un entero positivo');
        return;
      }
      if (n > 9_999_999) {
        setErrorLocal('El número de factura no puede superar 9.999.999');
        return;
      }
    }
    setErrorLocal(null);
    onConfirmar(fecha, numFactura ? parseInt(numFactura, 10) : null);
  };

  const errorVisible = errorCreacion || errorLocal;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <h2 className="text-base font-bold text-gray-900">Nueva factura</h2>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Nº de factura</label>
          <input
            type="number"
            value={numFactura}
            onChange={e => { setNumFactura(e.target.value); setErrorLocal(null); }}
            placeholder="Automático"
            min="1"
            max="9999999"
            className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-400 mt-1">Déjalo vacío para usar el siguiente número automático</p>
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">Fecha de la factura</label>
          <input
            type="date"
            value={fecha}
            onChange={e => setFecha(e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {errorVisible && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2.5 flex items-start gap-2">
            <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
            {errorVisible}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onCancelar}
            disabled={creando}
            className="flex-1 border border-gray-300 text-gray-700 font-medium py-2.5
                       rounded-xl text-sm hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirmar}
            disabled={creando || !fecha}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold
                       py-2.5 rounded-xl text-sm transition disabled:opacity-50
                       flex items-center justify-center gap-2"
          >
            {creando && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            )}
            {creando ? 'Creando...' : 'Crear factura'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FacturasList() {
  const navigate = useNavigate();
  const [facturas, setFacturas]         = useState([]);
  const [isLoading, setIsLoading]       = useState(false);
  const [error, setError]               = useState(null);
  const [creando, setCreando]             = useState(false);
  const [modalAbierto, setModalAbierto]   = useState(false);
  const [errorCreacion, setErrorCreacion] = useState(null);
  const [busqueda, setBusqueda]     = useState('');
  const [filtroMes, setFiltroMes]   = useState('');
  const [filtroAnyo, setFiltroAnyo] = useState('');
  const [filtroDia, setFiltroDia]   = useState('');

  const cargar = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await facturasService.listar();
      setFacturas(res.data);
    } catch {
      setError('Error al cargar las facturas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleNuevaFactura = async (fecha, numFactura) => {
    setCreando(true);
    setErrorCreacion(null);
    try {
      const res = await facturasService.crear(fecha, numFactura);
      setModalAbierto(false);
      navigate(`/facturas/${res.data.id}`);
    } catch (err) {
      setErrorCreacion(err.response?.data?.error || 'Error al crear la factura');
      setCreando(false);
    }
  };

  const anyosDisponibles = [...new Set(facturas.map(f => f.fecha?.slice(0, 4)).filter(Boolean))].sort().reverse();
  const diasEnMes = filtroMes && filtroAnyo
    ? new Date(Number(filtroAnyo), Number(filtroMes), 0).getDate()
    : 31;

  const facturasFiltradas = facturas.filter(f => {
    if (busqueda.trim() && !String(f.numFactura).includes(busqueda.trim())) return false;
    if (filtroAnyo && f.fecha && !f.fecha.startsWith(filtroAnyo)) return false;
    if (filtroMes && f.fecha && f.fecha.slice(5, 7) !== filtroMes) return false;
    if (filtroDia && f.fecha && f.fecha.slice(8, 10) !== filtroDia) return false;
    return true;
  });

  return (
    <>
      {modalAbierto && (
        <ModalNuevaFactura
          onConfirmar={handleNuevaFactura}
          onCancelar={() => { setModalAbierto(false); setErrorCreacion(null); }}
          creando={creando}
          errorCreacion={errorCreacion}
        />
      )}
      <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">

        {/* ── Cabecera ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Facturas OCASO</h1>
            <p className="text-sm text-gray-500">{facturas.length} facturas</p>
          </div>
          <button
            onClick={() => setModalAbierto(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700
                       text-white font-semibold px-4 py-2 rounded-xl text-sm
                       transition shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
            </svg>
            Nueva factura
          </button>
        </div>

        {/* ── Filtros ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-3">
          {/* Nº factura */}
          <div className="flex items-center gap-3">
            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0"/>
            </svg>
            <input
              type="text"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar por nº de factura..."
              className="flex-1 text-sm focus:outline-none placeholder-gray-400"
            />
            {busqueda && (
              <button onClick={() => setBusqueda('')} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
            )}
          </div>

          {/* Filtro fecha: día + mes + año */}
          <div className="flex gap-2">
            <select
              value={filtroAnyo}
              onChange={e => { setFiltroAnyo(e.target.value); setFiltroDia(''); }}
              className="w-24 border border-gray-200 rounded-xl px-2 py-2.5 text-sm text-gray-700
                         focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Año</option>
              {anyosDisponibles.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <select
              value={filtroMes}
              onChange={e => { setFiltroMes(e.target.value); setFiltroDia(''); }}
              className="w-36 border border-gray-200 rounded-xl px-2 py-2.5 text-sm text-gray-700
                         focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Mes</option>
              {['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
                .map((m, i) => (
                  <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
                ))}
            </select>
            <select
              value={filtroDia}
              onChange={e => setFiltroDia(e.target.value)}
              disabled={!filtroMes}
              className="w-20 border border-gray-200 rounded-xl px-2 py-2.5 text-sm text-gray-700
                         focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white
                         disabled:opacity-40"
            >
              <option value="">Día</option>
              {Array.from({ length: diasEnMes }, (_, i) => String(i + 1).padStart(2, '0')).map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {(filtroAnyo || filtroMes || filtroDia) && (
              <button
                onClick={() => { setFiltroAnyo(''); setFiltroMes(''); setFiltroDia(''); }}
                className="px-3 py-2.5 text-gray-400 hover:text-gray-600 border border-gray-200
                           rounded-xl text-xs transition shrink-0">
                Limpiar
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {/* ── Lista ── */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"/>
          </div>
        ) : facturasFiltradas.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100
                          p-12 text-center text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <p className="font-medium">{busqueda ? 'Sin resultados' : 'No hay facturas'}</p>
            {!busqueda && <p className="text-sm mt-1">Crea la primera con el botón "Nueva factura"</p>}
          </div>
        ) : (
          <>
            {/* Tabla desktop */}
            <div className="hidden sm:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Nº Factura</th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">Fecha</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Siniestros</th>
                    <th className="text-right px-5 py-3 font-semibold text-gray-600">Neto</th>
                    <th className="text-right px-5 py-3 font-semibold text-gray-600">IVA</th>
                    <th className="text-right px-5 py-3 font-semibold text-gray-600">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {facturasFiltradas.map(f => (
                    <tr
                      key={f.id}
                      onClick={() => navigate(`/facturas/${f.id}`)}
                      className="cursor-pointer hover:bg-blue-50 transition group"
                    >
                      <td className="px-5 py-3.5">
                        <span className="font-bold text-blue-700 text-base group-hover:underline">
                          #{f.numFactura}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-gray-700">
                        {f.fecha ? new Date(f.fecha).toLocaleDateString('es-ES') : '—'}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full
                                         bg-gray-100 text-gray-600 font-semibold text-xs">
                          {f.numLineas}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right text-gray-700">{fmt(f.importeTotal)} €</td>
                      <td className="px-5 py-3.5 text-right text-amber-600">{fmt(f.ivaTotal)} €</td>
                      <td className="px-5 py-3.5 text-right font-bold text-green-700">{fmt(f.totalTotal)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards móvil */}
            <div className="sm:hidden space-y-2">
              {facturasFiltradas.map(f => (
                <div
                  key={f.id}
                  onClick={() => navigate(`/facturas/${f.id}`)}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 cursor-pointer
                             active:scale-95 transition"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-lg font-bold text-blue-700">Factura #{f.numFactura}</span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {f.fecha ? new Date(f.fecha).toLocaleDateString('es-ES') : '—'}
                        {' · '}{f.numLineas} siniestro{f.numLineas !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-700">{fmt(f.totalTotal)} €</p>
                      <p className="text-xs text-gray-400">total</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
